

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  createFetchError,
  extractErrorDetails,
  FetchError
} from '../api/fetch-error.utils';
import { AuthService } from '../auth/auth.service';
import { ChatNotificationService } from '../chat/ui/chat-notification.service';
import { ConfigurationClientService } from './configuration-client.service';

export abstract class ApiClient {
  private readonly className = ApiClient.name;

  protected auth = inject(AuthService);
  protected notificationService = inject(ChatNotificationService);

  constructor(
    protected config: ConfigurationClientService,
    protected http: HttpClient,
  ) { }

  abstract get baseUrl(): string;

  protected getBackendAPIUrl() {
    return this.config.getBackendAPIUrl();
  }

  /**
   * Common error handler for HTTP requests.
   */
  protected handleHttpError(error: HttpErrorResponse): Observable<never> {
    console.error('HTTP Error:', {
      status: error.status,
      message: error.message,
      body: error.error,
    });

    let errorMessage = 'An unexpected error occurred.';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server returned code ${error.status}, message: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Creates a custom error object for streaming errors.
   */
  protected createCustomError(error: any): Error {
    return new Error(`Streaming error: ${error.message || 'Unknown error'}`);
  }

  /**
   * A wrapper around the fetch API that adds the Authorization header and workspace context.
   * @param url The URL to fetch.
   * @param options The fetch options.
   * @returns The fetch response as a Promise.
   */
  protected async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    try {

      console.log(`Fetching URL: ${url}`, options);

      // Check if the URL matches the backend URL (or skip if needed)
      const backendUrl = environment.BACKEND_API_URL || this.config.getBackendAPIUrl();
      if (!url.startsWith(backendUrl)) {
        return fetch(url, options); // Skip if not targeting the backend
      }

      const profile = await this.auth.getProfile();

      // Get the current session from Supabase
      const access_token = await this.auth.getAccessToken();

      // Add the Authorization header if a session exists
      const headers = new Headers(options.headers || {});
      if (access_token) {
        headers.set('Authorization', `Bearer ${access_token}`);
      }

      // Add workspace id
      const workspaceId = profile.selected_workspace;
      if (workspaceId) {
        headers.set('x-workspace-id', `${workspaceId}`);
      }

      // Perform the fetch request with modified headers
      return fetch(url, {
        ...options,
        headers,
      });
    } catch (error: any) {
      // Check for network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        // Create a network error response similar to HTTP interceptor
        const networkError = new Response(null, {
          status: 0,
          statusText: 'Network Error',
        });
        return networkError;
      }
      throw error;
    }
  }

  /**
   * Creates a structured error from a fetch response with notifications
   */
  protected async handleFetchError(
    response: Response,
    showNotification = true,
  ): Promise<FetchError> {
    const error = await createFetchError(response);

    // Log detailed error for debugging (with full stack trace)
    console.error('API Error Details:', {
      ...extractErrorDetails(error),
      url: response.url,
      correlationId: error.body?.correlationId,
    });

    // Show user-friendly notification if enabled (default: true)
    if (showNotification) {
      this.notificationService.showApiError(error);
    }

    return error;
  }

  /**
   * Handles generic errors (network, etc.) with notifications
   */
  protected handleGenericError(error: any, showNotification = true): Error {
    console.error('Generic error:', error);

    const genericError = new Error(error.message || 'Failed to connect to the server');

    if (showNotification) {
      if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
        this.notificationService.notifyConnectionError();
      } else {
        this.notificationService.showError(genericError.message);
      }
    }

    return genericError;
  }
}
